import { useTranslation } from "react-i18next";
import { List } from "@mui/material";
import statusService from "api/statusService";
import tagsService from "api/tagsService";
import PackageSkeleton from "components/PackageSkeleton";
import KeysContext from "contexts/KeysContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";
import NotFoundScreen from "components/NotFoundScreen";
import IssuesContext from "contexts/IssuesContext";
import { useInfiniteQuery } from "react-query";
import { axiosInstance } from "api/axios";
import { IIssue } from "dto/Helpdesk/IIssue";
import HelpdeskIssueCard from "components/HelpdeskIssueCard";
import ChatModal from "components/ChatModal";

export const IssuesList = ({
  debouncedText,
  searchText,
}: {
  debouncedText: string;
  searchText: string;
}) => {
  const { t } = useTranslation();
  const { issues, setIssues } = useContext(IssuesContext);
  const [selectedElements, setSelectedElements] = useState<IIssue[]>([]);

  const scrollWrapper = useRef(null);

  const [lastFetchTime, setLastFetchTime] = useState(0);
  const fetchThreshold = 1000; // 1 second

  const { keys } = useContext(KeysContext);

  const { data: tagsData } = tagsService.useGetTags();
  const { data: statusesData } = statusService.useGetStatuses();

  const statuses = useMemo(() => statusesData?.data ?? [], [statusesData]);
  const tags = useMemo(() => tagsData?.data ?? [], [tagsData]);
  const separatorRef = useRef();
  const [isInitialized, setIsInitialized] = useState(false);
  const [chatId, setChatId] = useState<string>(null);

  // Jedna firma na użytkownika, bez przełącznika kontekstu (decyzja D1) — nie ma już
  // zdarzenia "zmiana firmy" w trakcie sesji, więc czyszczenie czatu/zaznaczenia po takiej
  // zmianie jest zbędne (dawne useOnProfileChange nigdy by się tu nie odpaliło).

  const { data, fetchNextPage, isLoading, isFetchingNextPage } =
    useInfiniteQuery(
      ["issues", debouncedText],
      async ({ pageParam = "" }) => {
        const query = new URLSearchParams(window.location.search);

        query.append("Page", pageParam !== "" ? pageParam : "0");

        if (searchText.length) {
          query.append("Text", debouncedText);
        }

        const res = await axiosInstance.get(`/issues?${query}`);
        return res.data;
      },
      {
        enabled: false,
        staleTime: 0,
        cacheTime: 0,
        getNextPageParam: (lastPage) => {
          if (!lastPage.LastPage) {
            return lastPage.Page + 1;
          }
          return undefined;
        },
      },
    );

  const handleLoadNextPage = () => {
    if (!separatorRef.current || isLoading) return;

    const rect = separatorRef.current.getBoundingClientRect();

    const isSeparatorVisible =
      rect.top * 0.5 < window.innerHeight && rect.bottom >= 0;

    const currentTime = Date.now();
    if (currentTime - lastFetchTime < fetchThreshold) return;

    if (isSeparatorVisible) {
      setLastFetchTime(currentTime);
      fetchNextPage();
    } else if (scrollWrapper.current.scrollTop < 10) {
      //setLastFetchTime(currentTime);
      //fetchPrevPage();
    }
  };

  const compileData = async () => {
    if (!data) return;

    const packs = await Promise.all(
      data?.pages
        .map((page) => {
          return page.Items;
        })
        .flat(),
    );

    setIssues(packs.flat());
    setIsInitialized(true);
  };

  useEffect(() => {
    compileData();
  }, [data]);

  useEffect(() => {
    if (!isInitialized) {
      fetchNextPage();
    }
  }, []);

  const handleDobleClick = (issue: IIssue) => {
    setChatId(issue.ChatId);
  };

  if (isLoading && !isFetchingNextPage) {
    return (
      <div>
        {Array.from({ length: 100 }, (_, index) => (
          <PackageSkeleton key={index} />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className={styles.issueList}>
        <List
          ref={scrollWrapper}
          onScroll={(e) => {
            handleLoadNextPage(e);
          }}
          sx={{
            width: "100%",
            position: "relative",
            overflow: "auto",
            maxHeight: "calc(100% )",
            "& ul": { padding: 0 },
          }}
        >
          {!issues.length &&
            !isLoading &&
            !isFetchingNextPage &&
            isInitialized && (
              <div style={{ height: "calc(100vh - 180px)" }}>
                <NotFoundScreen text={t("emptyStates.noResultsSuffix")} />
              </div>
            )}
          {issues.map((pack) => {
            return (
              <div
                key={pack.Id}
                className={styles.packe}
                onDoubleClick={() => handleDobleClick(pack)}
              >
                <HelpdeskIssueCard
                  scrollWrapper={scrollWrapper}
                  key={pack.Id}
                  onPreview={() => {}}
                  isFirst={false}
                  selectedElements={selectedElements}
                  setSelectedElements={setSelectedElements}
                  isSelectable={true}
                  selectMultiple={false}
                  keys={keys}
                  msg={pack}
                  statuses={statuses}
                  tags={tags}
                  decodedContent={undefined}
                />
              </div>
            );
          })}
          <div ref={separatorRef} style={{ height: "5px" }}></div>
        </List>
      </div>
      {chatId && (
        <div className={styles.modalBg}>
          <ChatModal chatId={chatId} toggle={() => setChatId(null)}></ChatModal>
        </div>
      )}
    </>
  );
};
